import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, CssBaseline, Drawer, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Tooltip, Collapse, Divider, Avatar, IconButton,
  Chip, useMediaQuery
} from '@mui/material';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import ScaleRoundedIcon from '@mui/icons-material/ScaleRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import BrandingWatermarkRoundedIcon from '@mui/icons-material/BrandingWatermarkRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../api';
import { useUser } from '../contexts/UserContext';
import Screensaver, { useScreensaver, ScreensaverToggle } from './Screensaver';

const navGroups = () => {
  const posItems = [
    { text: 'POS Terminal', path: '/pos', icon: <PointOfSaleRoundedIcon />, tone: '#2563eb' },
    { text: 'Billing History', path: '/orders', icon: <ReceiptLongRoundedIcon />, tone: '#0891b2' },
    { text: 'Hold Bills', path: '/hold-bills', icon: <PendingActionsRoundedIcon />, tone: '#db2777' },
  ];
  const visiblePos = posItems;
  return [
    {
      key: 'overview', label: 'Overview', tone: '#6366f1', items: [
        { text: 'Home', path: '/', icon: <HomeRoundedIcon />, tone: '#6366f1' },
        { text: 'POS Dashboard', path: '/dashboard', icon: <BarChartRoundedIcon />, tone: '#0ea5e9' },
        { text: 'Invoices Dashboard',  path: '/accounts/invoices-dashboard',  icon: <BarChartRoundedIcon />,    tone: '#0f766e' },
        { text: 'Online Overview',      path: '/accounts/online-overview',     icon: <BarChartRoundedIcon />,    tone: '#2563eb' },
        { text: 'Profit & Loss',       path: '/accounts/profit-loss',         icon: <BarChartRoundedIcon />,    tone: '#7c3aed' },
      ], collapsible: true, icon: <HomeRoundedIcon />
    },
    {
      key: 'pos', label: 'Point of Sale', tone: '#2563eb', items: visiblePos, collapsible: true, icon: <PointOfSaleRoundedIcon />
    },
    {
      key: 'invoices', label: 'Invoices', tone: '#0f766e', items: [
        { text: 'All Invoices', path: '/accounts/invoices', icon: <RequestQuoteRoundedIcon />, tone: '#0f766e' },
        { text: 'Create Sale', path: '/accounts/sale', icon: <ReceiptLongRoundedIcon />, tone: '#16a34a' },
        { text: 'Create Purchase', path: '/accounts/purchase', icon: <LocalShippingRoundedIcon />, tone: '#ea580c' },
        { text: 'Create PI', path: '/accounts/sale?type=pi', icon: <ReceiptLongRoundedIcon />, tone: '#7c3aed' },
        { text: 'Proforma Invoices', path: '/accounts/proforma-invoices', icon: <RequestQuoteRoundedIcon />, tone: '#db2777' },
      ], collapsible: true, icon: <RequestQuoteRoundedIcon />
    },
    {
      key: 'catalog', label: 'Catalog & Stock', tone: '#d97706', items: [
        { text: 'View Products', path: '/products', icon: <Inventory2RoundedIcon />, tone: '#d97706' },
        { text: 'Stock', path: '/stock', icon: <Inventory2RoundedIcon />, tone: '#65a30d' },
        { text: 'Inactive Products', path: '/products?preset=inactive-products', icon: <PendingActionsRoundedIcon />, tone: '#64748b' },
        { text: 'Archived Products', path: '/products?preset=archived-products', icon: <PendingActionsRoundedIcon />, tone: '#991b1b' },
        { text: 'Low Stocks', path: '/stock?preset=low-stock', icon: <NotificationsNoneRoundedIcon />, tone: '#dc2626' },
        { text: 'Bulk Update', path: '/products/bulk-update', icon: <Inventory2RoundedIcon />, tone: '#ca8a04' },
      ], collapsible: true, icon: <Inventory2RoundedIcon />
    },
    {
      key: 'accounts', label: 'Accounts & Finance', tone: '#7c3aed', items: [
        { text: 'Party Ledger', path: '/accounts/ledger', icon: <AccountBalanceWalletRoundedIcon />, tone: '#0f766e' },
        { text: 'Expenses', path: '/accounts/expenses', icon: <AccountBalanceWalletRoundedIcon />, tone: '#dc2626' },
        { text: 'Reports', path: '/accounts/reports', icon: <BarChartRoundedIcon />, tone: '#0891b2' },
      ], collapsible: true, icon: <AccountBalanceWalletRoundedIcon />
    },
    {
      key: 'settings', label: 'Configuration', tone: '#db2777', items: [
        { text: 'Categories', path: '/settings/categories', icon: <CategoryRoundedIcon />, tone: '#9333ea' },
        { text: 'Units', path: '/settings/units', icon: <ScaleRoundedIcon />, tone: '#2563eb' },
        { text: 'Taxes', path: '/settings/taxes', icon: <PercentRoundedIcon />, tone: '#d97706' },
        { text: 'Brands', path: '/settings/brands', icon: <BrandingWatermarkRoundedIcon />, tone: '#dc2626' },
      ], collapsible: true, icon: <SettingsRoundedIcon />
    },
  ];
};

const HeaderAction = ({ title, color, hoverBg, icon, onClick }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.1 }}>
    <IconButton
      onClick={onClick}
      title={title}
      size="small"
      sx={{ border: '1px solid #e2e8f0', borderRadius: 2, color, '&:hover': { bgcolor: hoverBg } }}
    >
      {icon}
    </IconButton>
    <Typography variant="caption" sx={{ fontSize: '0.42rem', lineHeight: 1, color: '#64748b', whiteSpace: 'nowrap' }}>{title}</Typography>
  </Box>
);

const titleForPath = (path) => {
  const map = {
    '/': ['Welcome', 'Operations overview'],
    '/dashboard': ['Dashboard', 'Sales and inventory overview'],
    '/pos': ['POS Terminal', 'Create and complete customer bills'],
    '/orders': ['Billing History', 'Review completed and held bills'],
    '/hold-bills': ['Hold Bills', 'Resume or discard held bills'],
    '/products': ['Products', 'Manage your retail catalog'],
    '/products?preset=inactive-products': ['Inactive Products', 'Products awaiting activation'],
    '/products?preset=archived-products': ['Archived Products', 'Restore archived catalog records when needed'],
    '/products/bulk-update': ['Bulk Update', 'Update catalog data efficiently'],
    '/stock': ['Stock', 'Current inventory position'],
    '/stock?preset=low-stock': ['Low Stocks', 'Variants with 10 or fewer items remaining'],
    '/accounts/sale': ['Sales', 'Create a sales transaction'],
    '/accounts/purchase': ['Purchases', 'Record supplier purchases'],
    '/accounts/invoices': ['Invoices', 'Manage sales and purchase invoices'],
    '/accounts/invoices-dashboard':               ['Invoices Dashboard', 'Sales and purchase invoice analytics'],
    '/accounts/online-overview':                   ['Online Overview', 'Online orders and customer analytics'],
    '/accounts/sale?type=pi': ['Create Proforma Invoice', 'Prepare a proforma invoice (PI)'],
    '/accounts/proforma-invoices': ['Proforma Invoices', 'Manage proforma invoices (PI)'],
    '/accounts/expenses': ['Expenses', 'Track operating expenses'],
    '/accounts/ledger': ['Party Ledger', 'Monitor outstanding balances and payments'],
    '/accounts/profit-loss': ['Profit & Loss', 'Monitor business performance'],
    '/accounts/reports': ['Reports', 'Business and financial reporting'],
    '/settings/categories': ['Categories', 'Organize your product catalog'],
    '/settings/units': ['Units', 'Configure measurement units'],
    '/settings/taxes': ['Taxes', 'Configure tax rules'],
    '/settings/brands': ['Brands', 'Manage product brands'],
  };
  return map[path] || ['OwnTrip', 'Point of sale and business management'];
};

export default function Layout({ children }) {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:900px)');
  const [isHovered, setIsHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('overview');

  const drawerWidth = 56;
  const expandedDrawerWidth = 196;
  const expanded = isMobile ? true : isHovered;
  const groups = useMemo(() => navGroups(), []);
  const [pageTitle, pageSubtitle] = titleForPath(`${location.pathname}${location.search}`);
  const displayName = user?.name || user?.username || user?.email || 'User';
  const initials = displayName.split(/\s+/).filter(Boolean).slice(0, 2).map(x => x[0]).join('').toUpperCase() || 'U';

  const isSelected = (path) => {
    const [pathname, search = ''] = path.split('?');
    if (search) return location.pathname === pathname && (location.search === `?${search}` || location.search.includes(search));
    return (location.pathname === pathname && !location.search) ||
      (pathname !== '/' && location.pathname.startsWith(`${pathname}/`));
  };

  useEffect(() => {
    const matchingGroup = groups.find(group => group.items.some(item => isSelected(item.path)));
    if (matchingGroup) setActiveMenu(matchingGroup.key);
    // Navigation context should follow the current route when opening a deep link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, groups]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const screensaver = useScreensaver();

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box className={`enterprise-sidebar-brand ${expanded ? 'is-expanded' : ''}`} sx={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
        {expanded ? (
          <Box component="img" src="/logo-transparent-expendable.png" alt="OwnTripPOS" className="enterprise-expanded-logo" />
        ) : (
          <Box component="img" src="/logo.png" alt="OwnTripPOS" className="enterprise-collapsed-logo" />
        )}
      </Box>
      <Divider sx={{ borderColor: '#e2e8f0' }} />

      <Box sx={{ flex: 1, overflowY: 'auto', py: .4 }}>
        {groups.map((group) => {
          const groupActive = group.items.some(i => isSelected(i.path));
          const open = activeMenu === group.key;
          return (
            <Box key={group.key}>
              {expanded && group.label && <div className="enterprise-nav-section">{group.label}</div>}
              {group.collapsible ? (
                <ListItem disablePadding>
                  <Tooltip title={expanded ? '' : group.label} placement="right" arrow>
                    <ListItemButton
                      onClick={() => expanded ? setActiveMenu(open ? null : group.key) : setActiveMenu(group.key)}
                      selected={groupActive}
                      sx={{ mx: 1, mb: .2, minHeight: 24, borderRadius: 1.5, justifyContent: expanded ? 'initial' : 'center', px: expanded ? 1 : .8, '--nav-color': group.tone }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: expanded ? 1 : 0, justifyContent: 'center', color: group.tone, '& svg': { fontSize: 13, color: group.tone } }}>{group.icon}</ListItemIcon>
                      {expanded && <ListItemText primary={group.label} primaryTypographyProps={{ fontSize: 8, fontWeight: 550, color: group.tone }} />}
                      {expanded && (open ? <ExpandLessRoundedIcon sx={{ fontSize: 11 }} /> : <ExpandMoreRoundedIcon sx={{ fontSize: 11, color: group.tone }} />)}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              ) : null}
              <Collapse in={!group.collapsible || (expanded && open)} timeout={180} unmountOnExit>
                <List disablePadding>
                  {group.items.map((item) => (
                    <ListItem key={item.path} disablePadding>
                      <Tooltip title={expanded ? '' : item.text} placement="right" arrow>
                        <ListItemButton
                          component={Link}
                          to={item.path}
                          selected={isSelected(item.path)}
                          onClick={() => isMobile && setMobileOpen(false)}
                          sx={{
                            mx: expanded ? 1 : .8,
                            mb: .2,
                            minHeight: 26,
                            pl: expanded && group.collapsible ? 3.5 : .8,
                            pr: 1,
                            borderRadius: 1.5,
                            justifyContent: expanded ? 'initial' : 'center',
                            '--nav-color': item.tone,
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 0, mr: expanded ? 1 : 0, justifyContent: 'center', color: item.tone, '& svg': { fontSize: 12, color: item.tone } }}>{item.icon}</ListItemIcon>
                          {expanded && <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 4, fontWeight: 550, color: '#1f2937' }} />}
                          {expanded && isSelected(item.path) && <ChevronRightRoundedIcon sx={{ fontSize: 11, color: item.tone }} />}
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      <Divider sx={{ borderColor: '#e2e8f0' }} />
      <List sx={{ py: .4 }}>
        <ListItem disablePadding>
          <Tooltip title={expanded ? '' : 'Logout'} placement="right" arrow>
            <ListItemButton onClick={handleLogout} sx={{ mx: 1, minHeight: 31, borderRadius: 1.5, color: '#fca5a5', justifyContent: expanded ? 'initial' : 'center', px: expanded ? 1 : .8, '&:hover': { background: 'rgba(239,68,68,.10) !important' } }}>
              <ListItemIcon sx={{ minWidth: 0, mr: expanded ? 1 : 0, color: 'inherit', justifyContent: 'center', '& svg': { fontSize: 13 } }}><LogoutRoundedIcon /></ListItemIcon>
              {expanded && <ListItemText primary="Sign out" primaryTypographyProps={{ fontSize: 4, fontWeight: 550, color: '#b91c1c' }} />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <Box component="img" src="/logo-transparent.png" alt="" aria-hidden="true" className="enterprise-watermark" />
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        className="enterprise-sidebar"
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
        sx={{
          width: expanded ? expandedDrawerWidth : drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: expanded ? expandedDrawerWidth : drawerWidth,
            boxSizing: 'border-box',
            transition: 'width .22s ease',
            overflowX: 'hidden',
            position: isMobile ? 'fixed' : 'fixed',
            height: '100vh',
            zIndex: 1200,
          },
        }}
      >{drawer}</Drawer>

      <Box component="main" className="enterprise-main" sx={{ flexGrow: 1, minWidth: 0, ml: isMobile ? 0 : 0, transition: 'padding-left .22s ease' }}>
        <Box className="enterprise-topbar">
          <ScreensaverToggle onShow={screensaver.show} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {isMobile && <IconButton size="small" onClick={() => setMobileOpen(true)} sx={{ mr: .5 }}><MenuRoundedIcon /></IconButton>}
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: .6 }}>
                <Typography className="enterprise-title" noWrap>{pageTitle}</Typography>
                <ChevronRightRoundedIcon sx={{ color: '#94a3b8', fontSize: 17 }} />
                <Typography className="enterprise-kicker" noWrap>{pageSubtitle}</Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HeaderAction title="POS" color="#2563eb" hoverBg="#eff6ff" icon={<PointOfSaleRoundedIcon fontSize="small" />} onClick={() => navigate('/pos')} />
            <HeaderAction title="Hold Bills" color="#db2777" hoverBg="#fdf2f8" icon={<PendingActionsRoundedIcon fontSize="small" />} onClick={() => navigate('/hold-bills')} />
            <HeaderAction title="Billing History" color="#0891b2" hoverBg="#ecfeff" icon={<ReceiptLongRoundedIcon fontSize="small" />} onClick={() => navigate('/orders')} />
            <Chip
              label="Live workspace"
              size="small"
              sx={{
                display: { xs: 'none', md: 'flex' },
                height: 28,
                color: '#166534',
                bgcolor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                fontSize: 11,
                fontWeight: 800,
                '& .MuiChip-label': { px: 1.2 },
              }}
            />
            <IconButton size="small" sx={{ border: '1px solid #e2e8f0', borderRadius: 2, color: '#64748b' }}><NotificationsNoneRoundedIcon fontSize="small" /></IconButton>
            <Chip
              avatar={<Avatar sx={{ bgcolor: '#eef2ff !important', color: '#4338ca !important', fontSize: 12, fontWeight: 800 }}>{initials}</Avatar>}
              label={displayName}
              sx={{ bgcolor: '#fff', border: '1px solid #dbe3ee', maxWidth: 180, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 750 } }}
            />
          </Box>
        </Box>
        <Box className="enterprise-content">
          <Box className="enterprise-page">{children}</Box>
        </Box>
        <Box component="footer" sx={{ px: 2.5, pb: 1.2, color: '#94a3b8', fontSize: 11, textAlign: 'right' }}>
          © {new Date().getFullYear()} Owntrip (OPC) Pvt Ltd. · OwnTrip
        </Box>
        <Screensaver visible={screensaver.visible} onHide={screensaver.hide} />
      </Box>
    </Box>
  );
}
